package se.mittpass.web.components

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.SlotDto

@Composable
internal fun MonthView(
    anchor: String,
    slots: List<SlotDto>,
    isManager: Boolean,
    currentUserId: Long?,
    isMobile: Boolean,
    staff: List<se.mittpass.shared.api.StaffMemberDto> = emptyList(),
    onDayClick: (day: String) -> Unit,
    onEdit: (SlotDto) -> Unit,
    onShowDay: (day: String, slots: List<SlotDto>) -> Unit,
    absences: List<se.mittpass.shared.api.AbsenceRequestDto> = emptyList()
) {
    val (gridStart, _) = monthGridRange(anchor)
    val anchorMonth = anchor.split("-")[1].toInt()
    val today = todayLocalDate()
    val slotsByDay = remember(slots) { slots.groupBy { it.day } }

    var selectedMobileDay by remember { mutableStateOf<String?>(null) }

    val cellMinHeight = if (isMobile) "52px" else "108px"

    Div({
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "1rem")
            property("width", "100%")
        }
    }) {
        Div({ style {
            property("display", "grid")
            property("grid-template-columns", "repeat(7, minmax(0, 1fr))")
            property("gap", if (isMobile) "2px" else "1px")
            property("background", "var(--rp-overlay)")
            property("border-radius", "16px")
            property("overflow", "hidden")
            property("border", "1px solid var(--rp-hl-med)")
            property("width", "100%")
            property("box-sizing", "border-box")
        } }) {
            // Day-of-week header
            for (i in 0..6) {
                Div({ style {
                    property("background", "var(--rp-surface)")
                    property("padding", if (isMobile) "0.35rem 0.1rem" else "0.5rem")
                    property("font-size", if (isMobile) "0.65rem" else "0.7rem")
                    property("font-weight", "800")
                    property("color", "var(--rp-subtle)"); property("text-transform", "uppercase")
                    property("letter-spacing", "0.5px"); property("text-align", "center")
                } }) { Text(DAY_NAMES_SHORT[i]) }
            }

            // Cells
            for (idx in 0 until 42) {
                val cellDate = addDays(gridStart, idx)
                val parts = cellDate.split("-")
                val cellMonth = parts[1].toInt()
                val dayNum = parts[2].toInt()
                val isOtherMonth = cellMonth != anchorMonth
                val isToday = cellDate == today
                val daySlots = slotsByDay[cellDate].orEmpty().sortedBy { parseMinutes(it.startTime) }
                val isSelected = isMobile && cellDate == selectedMobileDay

                val dayAbsences = absences.filter { a ->
                    (a.status == "approved" || a.status == "pending") &&
                    a.startDate <= cellDate && a.endDate >= cellDate
                }

                Div({
                    onClick {
                        if (isMobile) {
                            selectedMobileDay = cellDate
                            onShowDay(cellDate, daySlots)
                        } else {
                            onDayClick(cellDate)
                        }
                    }
                    style {
                        property("background", if (isSelected) "var(--rp-info-bg)" else if (isOtherMonth) "var(--rp-surface)" else "var(--rp-surface)")
                        property("padding", if (isMobile) "0.3rem 0.15rem" else "0.4rem 0.45rem")
                        property("min-height", cellMinHeight)
                        property("cursor", "pointer")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("align-items", if (isMobile) "center" else "stretch")
                        property("justify-content", if (isMobile) "space-between" else "flex-start")
                        property("gap", "0.2rem")
                        property("position", "relative")
                        if (isSelected) property("outline", "2px solid var(--rp-pine)")
                        if (isOtherMonth) property("opacity", "0.45")
                    }
                }) {
                    // Day Number Header
                    Div({ style {
                        property("display", "flex"); property("align-items", "center")
                        property("justify-content", if (isMobile) "center" else "space-between")
                        property("width", "100%")
                    } }) {
                        Span({ style {
                            property("display", "inline-flex"); property("align-items", "center"); property("justify-content", "center")
                            property("min-width", if (isMobile) "22px" else "24px")
                            property("height", if (isMobile) "22px" else "24px")
                            property("border-radius", "50%")
                            property("font-size", if (isMobile) "0.8rem" else "0.78rem")
                            property("font-weight", if (isToday || isSelected) "800" else "600")
                            if (isToday) {
                                property("background", "var(--rp-pine)"); property("color", "var(--rp-surface)")
                            } else {
                                property("color", if (isOtherMonth) "var(--rp-muted)" else "var(--rp-text)")
                            }
                        } }) { Text(dayNum.toString()) }

                        if (!isMobile && daySlots.isNotEmpty()) {
                            Span({ style {
                                property("font-size", "0.65rem"); property("font-weight", "600"); property("color", "var(--rp-muted)")
                            } }) { Text("${daySlots.size}") }
                        }
                    }

                    // Mobile view: Compact dot and badge indicators
                    if (isMobile) {
                        Div({
                            style {
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("gap", "3px")
                                property("flex-wrap", "wrap")
                                property("min-height", "10px")
                            }
                        }) {
                            val unassignedCount = daySlots.count { it.assignedToUserId == null }
                            val assignedCount = daySlots.count { it.assignedToUserId != null }

                            if (unassignedCount > 0) {
                                Div({
                                    style {
                                        property("width", "6px"); property("height", "6px")
                                        property("border-radius", "50%"); property("background", "var(--rp-gold)")
                                    }
                                }) {}
                            }
                            if (assignedCount > 0) {
                                Div({
                                    style {
                                        property("width", "6px"); property("height", "6px")
                                        property("border-radius", "50%"); property("background", "var(--rp-pine)")
                                    }
                                }) {}
                            }
                            if (dayAbsences.isNotEmpty()) {
                                Div({
                                    style {
                                        property("width", "6px"); property("height", "6px")
                                        property("border-radius", "50%"); property("background", "var(--rp-iris)")
                                    }
                                }) {}
                            }
                        }
                    } else {
                        // Desktop View: Detailed Badges
                        dayAbsences.take(2).forEach { abs ->
                            val typeLabel = when (abs.type) { "vacation" -> "Semester"; "sick" -> "Sjuk"; "vab" -> "VAB"; else -> "Frånvaro" }
                            val (bgCol, textCol) = when (abs.type) {
                                "vacation" -> "var(--rp-info-bg)" to "var(--rp-iris)"
                                "sick" -> "var(--rp-err-bg)" to "var(--rp-err-text)"
                                "vab" -> "var(--rp-warn-bg)" to "var(--rp-warn-text)"
                                else -> "var(--rp-overlay)" to "var(--rp-text)"
                            }
                            val staffName = listOfNotNull(abs.firstName, abs.lastName).joinToString(" ").ifEmpty { abs.userEmail }
                            Div({ style {
                                property("background", bgCol); property("color", textCol)
                                property("padding", "0.15rem 0.4rem"); property("border-radius", "6px")
                                property("font-size", "0.68rem"); property("font-weight", "700")
                                property("white-space", "nowrap"); property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("display", "flex"); property("align-items", "center"); property("gap", "0.3rem")
                            } }) {
                                FaIcon("fa-solid fa-umbrella-beach", size = "0.65rem", color = textCol)
                                Text("$staffName ($typeLabel)")
                            }
                        }

                        val visible = daySlots.take(3)
                        visible.forEach { slot ->
                            val claimed = slot.assignedToUserId != null
                            val hasInterest = !claimed && slot.interestedUsers.isNotEmpty()
                            val isUserInterested = !claimed && slot.interestedUsers.any { it.id == currentUserId }
                            val claimedScheme = if (claimed) getStaffColorScheme(staff, slot.assignedToUserId) else null
                            Div({
                                onClick { ev ->
                                    ev.stopPropagation()
                                    if (isManager) onEdit(slot) else onShowDay(cellDate, daySlots)
                                }
                                style {
                                    property("display", "flex"); property("align-items", "center"); property("gap", "0.3rem")
                                    property("padding", "0.15rem 0.4rem"); property("border-radius", "6px")
                                    property("font-size", "0.7rem"); property("font-weight", "600")
                                    property("white-space", "nowrap"); property("overflow", "hidden"); property("text-overflow", "ellipsis")
                                    property("cursor", "pointer")
                                    when {
                                        claimed && claimedScheme != null -> {
                                            property("background", claimedScheme.bg); property("color", claimedScheme.text)
                                        }
                                        isManager && hasInterest -> {
                                            property("background", "var(--rp-warn-bg)"); property("color", "var(--rp-gold)")
                                        }
                                        !isManager && isUserInterested -> {
                                            property("background", "var(--rp-warn-bg)"); property("color", "var(--rp-gold)")
                                        }
                                        else -> {
                                            property("background", "var(--rp-info-bg)"); property("color", "var(--rp-pine)")
                                        }
                                    }
                                    if (isOtherMonth) property("opacity", "0.65")
                                }
                            }) {
                                Span({ style {
                                    property("width", "5px"); property("height", "5px"); property("border-radius", "50%")
                                    val dotBg = when {
                                        claimed && claimedScheme != null -> claimedScheme.primary
                                        claimed -> "var(--rp-ok-text)"
                                        hasInterest || isUserInterested -> "var(--rp-warn-text)"
                                        else -> "var(--rp-pine)"
                                    }
                                    property("background", dotBg); property("flex-shrink", "0")
                                } }) {}
                                val labelText = when {
                                    claimed -> "${slot.startTime.take(5)} ${slot.assignedToEmail?.substringBefore("@") ?: ""}"
                                    isUserInterested -> "${slot.startTime.take(5)} Sökt"
                                    isManager && hasInterest -> "${slot.startTime.take(5)} (${slot.interestedUsers.size} sökande)"
                                    else -> "${slot.startTime.take(5)} Öppen"
                                }
                                Text(labelText.trimEnd())
                            }
                        }
                        if (daySlots.size > 3) {
                            Div({
                                onClick { ev -> ev.stopPropagation(); onShowDay(cellDate, daySlots) }
                                style {
                                    property("font-size", "0.65rem"); property("font-weight", "700")
                                    property("color", "var(--rp-subtle)"); property("padding", "0.1rem 0.4rem")
                                    property("cursor", "pointer")
                                }
                            }) { Text("+${daySlots.size - 3} fler") }
                        }
                    }
                }
            }
        }
    }
}
